import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ArrowRight,
  Clock,
  Sparkles,
  FolderTree,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';

/**
 * Tier-1 post q14 targeting "batch organize incoming documents per client
 * automatically". Shape-A high-product-fit query. Lead: point Sortio at the
 * intake folder, one rule reads each doc, identifies the client, files it
 * under that client folder; batch run plus watch folder for ongoing arrivals,
 * and promote stable flows to deterministic Rule Builder rules. Covers
 * high-volume intake, per-client/per-matter structure, watch folders, and
 * when to drop AI for a rule. Audience: accounting firms, law firms, agencies,
 * property managers, anyone with per-client intake.
 */

const PUBLISHED = '2026-06-05';
const UPDATED = '2026-06-05';
const CANONICAL = 'https://www.getsortio.com/blog/batch-organize-documents-per-client';

const FAQ = [
  {
    question: 'How do you batch organize incoming documents per client automatically?',
    answer:
      'Point Sortio at the folder where documents arrive and give it one rule: read each file, identify the client it belongs to from the content, and file it under that client folder. Sortio processes the whole batch in a single run, and as a watch folder it keeps doing it on every new arrival without anyone opening the app, so each client folder stays current on its own.',
  },
  {
    question: 'How does the tool know which client a document belongs to?',
    answer:
      'It reads the content, not the filename. A scanned invoice named scan_0421.pdf still has the client name, an account number, a matter reference, or an address inside it. Sortio sends the extracted text to an LLM, which matches it against your list of known clients and returns the best match. Because the match is semantic, "Acme Corp", "Acme Corporation LLC", and an Acme invoice that only shows the account number all resolve to the same client folder. You can give Sortio your client roster in the prompt so it never invents a new folder for an existing client.',
  },
  {
    question: 'Can it handle a high-volume intake folder with hundreds of files a day?',
    answer:
      'Yes. A batch run reads every file in the intake folder in one pass and shows you a single preview of where each one will go. For ongoing high volume, set a watch folder on an hourly or daily trigger so the batch runs on a schedule instead of file-by-file. Once a flow is stable and the routing is deterministic enough that you no longer need the AI to make a judgment call, promote it to an AI Rule Builder rule, which does not use your AI allowance at all.',
  },
  {
    question: 'What if a document could belong to more than one client, or to none?',
    answer:
      'Tell the rule what to do with ambiguity. The recommended pattern is to route anything the model is not confident about to a _Needs Review folder rather than guessing. Sortio surfaces low-confidence matches in the preview, so during a batch run you simply reassign those few files before applying. For watch folders, run in Preview mode for the first week so you can confirm the matches before anything moves.',
  },
  {
    question: 'Can I file by matter or project under each client, not just by client?',
    answer:
      'Yes. A per-client, per-matter structure is just a deeper template: Clients/{Client}/{Matter}/{YYYY}/. The rule reads both the client and the matter or project reference from the document and builds the path. Law firms use client/matter, accounting firms use client/year, agencies use client/project. Sortio reads whatever identifier is in the document and routes to the matching subfolder.',
  },
  {
    question: 'Does this work for scanned paper, or only digital PDFs?',
    answer:
      'Both, as long as the scan has a text layer. Scans saved as searchable PDFs (the default on most office scanners) route by client the same way born-digital files do. Image-only scans need a one-time OCR pass first; OCRmyPDF is a free tool that adds the text layer in bulk, and Sortio reads the layer it produces. For a deeper walkthrough see our post on organizing scanned documents by content.',
  },
  {
    question: 'Is it safe to run this on confidential client files?',
    answer:
      'Sortio is preview-before-apply, so nothing moves until you approve it, and it keeps backup archives of your most recent sorts in case you need to undo a sort from inside the app. For confidentiality, Sortio supports local-only inference through Ollama (Llama 3, Mistral) so client documents never leave the machine. Managed AI or bring-your-own-key are also available if you prefer hosted inference.',
  },
];

const BatchOrganizeDocumentsPerClientBlogPage = () => {
  const breadcrumbItems = [
    { label: 'Blog', href: null },
    { label: 'Batch organize documents per client', href: null },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: CANONICAL },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Batch organize documents per client',
        item: CANONICAL,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'How to Batch Organize Incoming Documents Per Client Automatically',
    description:
      'Point an AI file organizer at your intake folder and let one rule read each document, identify the client, and file it under that client folder. Batch run plus watch folder, with a copyable folder tree and Sortio routing prompt.',
    url: CANONICAL,
    datePublished: PUBLISHED,
    dateModified: UPDATED,
    about: {
      '@type': 'SoftwareApplication',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'CMG Labs LLC',
      url: 'https://www.getsortio.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title="How to Batch Organize Incoming Documents Per Client Automatically"
        description="Point an AI file organizer at your intake folder and let one rule read each document, identify the client, and file it under that client folder. Batch run plus watch folder, with a copyable folder tree and Sortio routing prompt."
        canonical={CANONICAL}
        schema={[breadcrumbSchema, articleSchema, faqSchema]}
        keywords="batch organize documents per client, organize incoming documents per client automatically, sort client documents automatically, auto file documents by client, per client folder automation, intake folder automation, batch organize documents per client mac"
        lastModified={UPDATED}
      />

      <div className="container mx-auto px-6 max-w-3xl py-16 lg:py-24">
        <Breadcrumbs items={breadcrumbItems} />

        <Link
          to="/articles"
          className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group"
        >
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>All posts</span>
        </Link>

        <header className="mb-10">
          <span className="inline-block px-3 py-1 bg-sortio-primary/10 text-sortio-primary text-xs font-medium rounded-lg uppercase tracking-wider mb-4">
            Blog
          </span>
          <h1 className="text-4xl lg:text-5xl font-semibold text-apple-50 mb-6 leading-tight">
            How to Batch Organize Incoming Documents Per Client Automatically
          </h1>
          <div className="flex items-center text-apple-400 text-sm gap-4">
            <span className="flex items-center">
              <Clock className="mr-2" size={14} />
              Published {new Date(PUBLISHED).toLocaleDateString()}
            </span>
            <span>Updated {new Date(UPDATED).toLocaleDateString()}</span>
            <span>10 min read</span>
          </div>
        </header>

        <article className="prose-content space-y-6 text-apple-200 leading-relaxed">
          <h2 className="text-2xl font-semibold text-apple-50 mt-4 mb-4">
            How to batch organize incoming documents per client automatically
          </h2>

          <p className="text-lg">
            To batch organize incoming documents per client automatically, point Sortio at the
            folder where documents arrive and give it one rule: read each file, identify the
            client it belongs to from the content, and file it under that client folder. Sortio
            processes the whole batch in a single run, and as a watch folder it keeps doing it on
            every new arrival without anyone opening the app. Each client folder stays current on
            its own, with no manual filing.
          </p>

          <p>
            That is the whole loop. The rest of this post is the detail that makes it hold up at
            volume: the folder structure that scales to hundreds of clients, how the content
            match works so it does not put the wrong file in the wrong client folder, how to set
            the watch folder for ongoing intake, and when to drop the AI entirely and run a
            deterministic rule instead.
          </p>

          <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-6 my-8 not-prose">
            <div className="flex items-start gap-3">
              <FolderTree className="text-sortio-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium text-apple-50 mb-2">The short version</h3>
                <p className="text-apple-300 text-sm leading-relaxed">
                  One intake folder, one rule, one preview. Sortio reads each document, matches it
                  to a client from the content (name, account number, matter reference, address),
                  and files it under that client folder using your template. Run it once to clear a
                  backlog, then set a watch folder so every new arrival routes itself. Promote the
                  stable flows to deterministic Rule Builder rules that do not use your AI
                  allowance at all.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            The structure: one intake folder, one folder per client
          </h2>

          <p>
            The mistake that makes per-client filing painful is letting documents pile up in
            Downloads, in email attachments, and in a scanner output folder all at once. The fix
            is one intake folder that everything lands in, and one stable destination tree of
            client folders that the intake feeds. The intake stays near empty. The client tree
            grows.
          </p>

          <p>
            For most teams the destination looks like this. Pick the depth that matches how you
            actually work: client then year for an accounting firm, client then matter for a law
            firm, client then project for an agency.
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`~/Documents/Intake/                  <- everything lands here first
~/Documents/Clients/
  Acme_Corp/
    2026/
      2026-04-12_Acme_Corp_Invoice.pdf
      2026-04-18_Acme_Corp_Signed_Contract.pdf
    2025/
  Bryce_Holdings/
    Matter_1042_Lease/
      2026-03-02_Bryce_Holdings_Lease.pdf
    Matter_1088_Dispute/
  Carver_Design/
    Project_Rebrand/
      2026-04-09_Carver_Design_Brief.pdf
  _Needs_Review/                     <- low-confidence matches land here`}
          </pre>

          <p>
            The naming convention inside each client folder is worth standardizing too, so files
            sort chronologically and stay readable when there are hundreds of them. A pattern that
            survives is date first, client next, then a short document type:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`{YYYY-MM-DD}_{Client}_{DocType}.pdf

2026-04-12_Acme_Corp_Invoice.pdf
2026-04-18_Acme_Corp_Signed_Contract.pdf
2026-03-02_Bryce_Holdings_Lease.pdf`}
          </pre>

          <p>
            The two pieces that matter most are the <em>_Needs_Review</em> folder and the ISO
            date prefix. The review folder is the safety valve: anything the model is not
            confident about goes there instead of into a wrong client folder, so a misfile never
            happens silently. The date prefix means Finder sorts every client folder by time with
            no custom view. Stanford University Libraries makes the same point about the value of
            consistent, descriptive naming, that{' '}
            <a
              href="https://guides.library.stanford.edu/data-best-practices"
              className="text-sortio-primary hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              it should be obvious where to find a file and what it contains
            </a>
            . That principle is exactly what a per-client structure encodes.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            How Sortio decides which client a document belongs to
          </h2>

          <p>
            The decision is made on content, not on the filename. An incoming document is almost
            never helpfully named. It is scan_0421.pdf, or document(3).pdf, or a vendor invoice
            number. But the client identity is inside the file: a company name on the header, an
            account number, a matter or case reference, a billing address, a contact name. Sortio
            extracts the text layer (image-only scans need an OCRmyPDF pass first), sends it to an
            LLM, and asks the model which of your known clients it matches.
          </p>

          <p>
            Because the match is semantic rather than a literal string compare, it is robust to
            the variation that breaks rigid rules. "Acme Corp", "Acme Corporation LLC", and an
            Acme statement that only prints the account number all resolve to the same client. You
            give Sortio the client roster in the prompt, so it routes to existing folders rather
            than inventing a new one for a name it has not seen. When the document does not clearly
            belong to anyone on the roster, the rule sends it to _Needs_Review instead of guessing.
          </p>

          <p>
            This is the difference between content-aware routing and the older approaches. A regex
            rule needs the client name to appear in a predictable place in predictable text. The
            moment a vendor reformats a statement, or a scan introduces OCR noise, the regex stops
            matching and the file falls through. Reading the meaning of the page holds up where the
            literal text shifts.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Manual vs rule-based vs AI-by-content
          </h2>

          <p>
            There are three honest ways to file documents per client. They are not strictly better
            or worse, they fit different volumes and different levels of variability. Here is the
            fair comparison.
          </p>

          <div className="overflow-x-auto not-prose my-6">
            <table className="w-full text-sm text-left border-collapse">
              <thead>
                <tr className="border-b border-apple-700/50">
                  <th className="py-3 pr-4 text-apple-50 font-semibold">Approach</th>
                  <th className="py-3 px-4 text-apple-50 font-semibold">How it routes</th>
                  <th className="py-3 px-4 text-apple-50 font-semibold">Best for</th>
                  <th className="py-3 pl-4 text-apple-50 font-semibold">Breaks down when</th>
                </tr>
              </thead>
              <tbody className="text-apple-300">
                <tr className="border-b border-apple-700/30">
                  <td className="py-3 pr-4 align-top font-medium text-apple-100">Manual filing</td>
                  <td className="py-3 px-4 align-top">A person opens each file and drags it to a client folder.</td>
                  <td className="py-3 px-4 align-top">A handful of files a week, or one-off oddities.</td>
                  <td className="py-3 pl-4 align-top">Volume rises. It is the first thing to fall behind under deadline.</td>
                </tr>
                <tr className="border-b border-apple-700/30">
                  <td className="py-3 pr-4 align-top font-medium text-apple-100">Rule-based (Hazel, File Juggler)</td>
                  <td className="py-3 px-4 align-top">Filename or text-layer patterns map to folders via rules you write.</td>
                  <td className="py-3 px-4 align-top">Stable, predictable sources where the same format arrives every time.</td>
                  <td className="py-3 pl-4 align-top">Formats vary across clients, OCR drifts, or you would need one rule per client.</td>
                </tr>
                <tr>
                  <td className="py-3 pr-4 align-top font-medium text-apple-100">AI-by-content (Sortio)</td>
                  <td className="py-3 px-4 align-top">An LLM reads each document and matches it to a client from meaning.</td>
                  <td className="py-3 px-4 align-top">Mixed formats, many clients, scanned intake, variable layouts.</td>
                  <td className="py-3 pl-4 align-top">Cost and speed: each AI sort draws from your AI allowance and takes longer than regex.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p>
            Hazel and File Juggler are genuinely good tools. Hazel is the long-standing macOS
            automation app and File Juggler is its closest Windows equivalent. Both can watch a
            folder and route by filename or by text matched against the document. If your intake is
            one or two sources that always look the same, a deterministic rule is the right tool and
            it runs essentially for free. The wall you hit with rule-based tools is variability:
            once you have dozens of clients sending dozens of formats, you are maintaining a rule
            library, and scanned documents with noisy OCR slip past the patterns. That is the gap
            content-aware AI fills. For the deeper version of this trade-off, see our piece on{' '}
            <Link to="/compare/filejuggler-vs-sortio" className="text-sortio-primary hover:underline">
              File Juggler vs Sortio
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            A working Sortio prompt for per-client routing
          </h2>

          <p>
            Drop this into the Sortio prompt box, point it at your intake folder, and run Preview
            before applying. Replace the client roster with your own. The roster is what keeps the
            model from inventing new folders for clients you already have.
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Read each document and identify which client it belongs
to. Match against this client roster (use the exact
folder name on the right):

  Acme Corporation, Acme / ACME LLC   -> Acme_Corp
  Bryce Holdings, Bryce LP            -> Bryce_Holdings
  Carver Design Studio, Carver        -> Carver_Design

Identify the client from the company name, account
number, matter or case reference, billing address, or
contact named in the document. Treat name variations as
the same client (Acme Corporation = Acme LLC).

File the document at:
  ~/Documents/Clients/{ClientFolder}/{YYYY}/
renamed to:
  {YYYY-MM-DD}_{ClientFolder}_{DocType}.pdf

DocType is a short label for what the document is:
Invoice, Statement, Contract, Lease, Letter, Form,
Receipt, or Other.

If you cannot confidently match the document to a client
on the roster, do NOT guess. Move it unchanged to
~/Documents/Clients/_Needs_Review/.

Use the date printed on the document (invoice date,
statement date, signing date). Dates in YYYY-MM-DD.`}
          </pre>

          <p>
            Click Preview. Sortio shows the proposed client, folder, and new name for every file,
            along with the identifier it matched on. Reassign any borderline case before applying.
            Apply commits the moves, and Sortio keeps backup archives of your most recent sorts
            in case you want to undo a sort from inside the app. For a per-matter or per-project
            structure, change the destination template to{' '}
            <span className="text-apple-100">{'~/Documents/Clients/{ClientFolder}/{Matter}/'}</span>{' '}
            and add a line telling the model to read the matter or project reference too.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Clearing a backlog with a single batch run
          </h2>

          <p>
            The first run is usually a backlog. Months of intake sitting in a shared drive, a
            scanner output folder, or an email-export dump, none of it filed. Point Sortio at that
            folder and run the prompt once. It reads every file in the batch, builds the full set of
            proposed moves, and shows you one preview covering all of them.
          </p>

          <p>
            The review pass is fast because you are not checking every file, you are checking the
            exceptions. Everything that matched a client cleanly you can trust at a glance. The work
            is the handful that landed in _Needs_Review or matched with low confidence. Reassign
            those, then apply. A backlog of several hundred documents typically clears in a few
            minutes of processing plus a few minutes of review, instead of an afternoon of dragging
            files. For the per-document side of this (reading scanned paper that has no useful name),
            see{' '}
            <Link
              to="/blog/organize-scanned-documents-by-content"
              className="text-sortio-primary hover:underline"
            >
              how to organize scanned documents by content
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Keeping it current with a watch folder
          </h2>

          <p>
            Clearing the backlog once is satisfying but it does not stay clean by itself. The
            ongoing version is a watch folder. On Sortio Pro ($14.99/month or $99/year) you attach
            the same prompt to the intake folder and Sortio runs it automatically as new files
            arrive. The setup is:
          </p>

          <ol className="list-decimal pl-5 space-y-2">
            <li>
              Designate the single intake folder everything lands in. Train the team (or your
              scanner, or an email rule) to drop new documents there and nowhere else.
            </li>
            <li>
              In Sortio, open Watch Folders, add the intake folder, and paste the per-client
              routing prompt with your roster.
            </li>
            <li>
              Set the trigger. "On new file" routes each document the moment it lands. For
              high-volume intake, an hourly or daily batch is calmer and easier to review.
            </li>
            <li>
              Run in Preview mode for the first week. Sortio queues the proposed moves and notifies
              you instead of applying them, so you can confirm the client matching is right before
              trusting it. After a clean week, switch to Apply.
            </li>
          </ol>

          <p>
            From there the intake folder approaches empty by default and every client folder stays
            current without anyone filing. The same idea scales to filing per client in batches
            across a whole team. For the team-level workflow, see{' '}
            <Link
              to="/blog/organize-client-tax-documents-accounting-firm"
              className="text-sortio-primary hover:underline"
            >
              how to organize client tax documents for an accounting firm
            </Link>
            , which covers the per-client, per-year structure and shared rules in more depth.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Promoting stable flows to deterministic rules
          </h2>

          <p>
            Not every routing decision needs an LLM forever. Once a flow is stable (one client
            always sends the same statement format from the same address, say), the routing is
            deterministic and you no longer need the model to make a judgment call. That is exactly
            the case to promote to the AI Rule Builder. You describe the rule in plain English and
            Sortio generates a deterministic config that does not use your AI allowance at all.
          </p>

          <p>
            The practical pattern at scale is a hybrid. Let AI-by-content handle the messy,
            high-variability part of your intake, the new clients, the scanned paper, the formats
            you have never seen, while a set of Rule Builder rules handles the predictable,
            high-volume flows for free. You get content-aware coverage where you need it and
            unmetered throughput where you do not. For a fuller treatment of when each one fits,
            see{' '}
            <Link
              to="/blog/ai-sort-vs-rule-builder-which-to-use"
              className="text-sortio-primary hover:underline"
            >
              AI Sort vs Rule Builder
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Confidentiality and reverting mistakes
          </h2>

          <p>
            Client documents are sensitive by definition, so two things matter. First, nothing is
            destructive: Sortio previews before it applies, and it keeps backup archives of your
            most recent sorts, so a misroute can be undone from inside the app. Second, for documents you
            do not want a third-party AI provider to see, Sortio supports local-only inference
            through Ollama (Llama 3, Mistral). The model runs on your machine and no file content
            leaves it. Managed AI and bring-your-own-key are available if you prefer hosted
            inference. For teams, Sortio for Teams ($29/seat/month) adds shared rules and an admin
            console so the same per-client routing applies consistently across everyone, which is
            the same discipline auditors expect. PCAOB AS 1215 frames it as documentation that lets{' '}
            <a
              href="https://pcaobus.org/oversight/standards/auditing-standards/details/AS1215"
              className="text-sortio-primary hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              an experienced person with no prior connection to the work understand it
            </a>
            , and a consistent per-client structure is how filing meets that bar.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            FAQ
          </h2>

          <div className="space-y-4 not-prose">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-apple-700/20 rounded-xl p-6">
                <h3 className="font-medium text-apple-50 mb-3">{f.question}</h3>
                <p className="text-apple-300 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </article>

        {/* Internal links */}
        <section className="mt-16 pt-8 border-t border-apple-700/30">
          <h2 className="text-xl font-semibold text-apple-50 mb-4">Keep reading</h2>
          <div className="grid gap-3">
            <Link
              to="/blog/automatically-rename-pdfs-by-content"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Automatically rename PDFs by content
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/organize-scanned-documents-by-content"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Organize scanned documents by content
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/organize-client-tax-documents-accounting-firm"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Organize client tax documents for an accounting firm
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </section>

        <section className="mt-12">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Try it on your intake folder</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              The free tier includes a one-time AI trial allowance. Point Sortio at a folder of
              unfiled client documents, run Preview, and see exactly which client folder each one
              would land in before anything moves. No credit card required.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Download Sortio
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default BatchOrganizeDocumentsPerClientBlogPage;
